/**
 * Cinematic replay video: plays the last ride's replay through the director's edit (src/game/cinematic.ts) with
 * its own cameras and slow motion, composites letterbox, watermark, titles and an end card on a 2D canvas, and
 * records that canvas plus the game audio with MediaRecorder. Real time: the replay is what is recorded.
 */
import * as THREE from 'three/webgpu';
import {
  CamAnchor, CineFormat, CineLength, CinePlan, FORMAT_SIZE, Shot, SLOW_SCALE, pickMime, planCinematic, shotAt, timeScaleAt, videoSeconds,
} from '../game/cinematic';
import { canFilmRide, topHighlight } from '../game/highlights';
import { featureLength } from '../sim/features';
import { rotate, vec } from '../sim/math';
import type { GameSession } from './session';

export interface CineOptions {
  format: CineFormat;
  length: CineLength;
  /** Title card: track name and time (already localized and formatted). */
  title: string;
  time: string | null;
  /** End card line under the logo. */
  endLine: string;
}

export interface CineProgress { phase: 'prepare' | 'record' | 'finish'; f: number }
export interface CineResult { blob: Blob; mime: string; seconds: number }

const LOGO = `${import.meta.env.BASE_URL}brand/megamotocross_logo_c.svg`;
const END_CARD = 2.6; // s
const TITLE_CARD = 2.8; // s

/** Trackside camera spots of a level: beside each jump and on the outside of each berm. */
export function levelAnchors(session: GameSession): CamAnchor[] {
  const level = session.world.level, line = level.line;
  if (!line) return [];
  const out: CamAnchor[] = [];
  for (const f of level.features) {
    const jump = 'refSpeed' in f;
    if (!jump && f.kind !== 'berm') continue;
    const s = line.wrap(f.s + featureLength(f) * (jump ? 0.6 : 0.5));
    const p = line.pointAt(s), t = line.tangentAt(s);
    // Berms: on the outside of the turn (left normal when curving right); jumps: on the left.
    const side = jump ? 1 : line.curvatureAt(s) > 0 ? -1 : 1;
    const d = jump ? 9 : 10;
    out.push({ x: p.x - t.z * d * side, z: p.z + t.x * d * side, kind: jump ? 'jump' : 'berm' });
  }
  return out;
}

const damp = (a: number, b: number, k: number, dt: number) => a + (b - a) * (1 - Math.exp(-k * dt));

/** The director's cameras, driven by the shot playing (set as the view's camera override). */
class CineCamera {
  private shot: Shot | null = null;
  private shotT = 0;
  private readonly pos = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  private snap = true;
  constructor(private readonly session: GameSession, private readonly vertical: boolean) {}

  setShot(s: Shot): void {
    if (s === this.shot) return;
    this.shot = s; this.shotT = 0; this.snap = true;
  }

  apply(cam: THREE.PerspectiveCamera, dt: number): void {
    const s = this.shot;
    if (!s) return;
    this.shotT += dt;
    const w = this.session.world, b = w.bike.position;
    const ground = (x: number, z: number) => w.terrain.heightAt(x, z);
    // Forward on the ground (yaw only).
    const f = rotate(w.bike.orientation, vec(0, 0, 1));
    const fl = Math.hypot(f.x, f.z) || 1, fx = f.x / fl, fz = f.z / fl;
    const v = this.vertical;
    let want: { x: number; y: number; z: number }, fov: number, lookY = 0.7, ease = 6;
    if (s.kind === 'trackside' && s.anchor) {
      const a = s.anchor;
      want = { x: a.x, y: ground(a.x, a.z) + 0.55, z: a.z };
      const dist = Math.hypot(b.x - a.x, b.z - a.z);
      // Long lens when the bike is far, wider as it passes (a fixed operator zooming).
      fov = THREE.MathUtils.clamp(THREE.MathUtils.radToDeg(2 * Math.atan((v ? 7 : 4.5) / Math.max(dist, 1))), v ? 30 : 16, v ? 75 : 55);
      ease = 1e3;
    } else if (s.kind === 'drone') {
      // High over the track behind the bike, drifting to one side: the track corridor is clear of tree crowns.
      const back = v ? 9 : 11, side = (s.dir ?? 1) * (1.5 + this.shotT * 0.9);
      want = { x: b.x - fx * back + fz * side, y: b.y + (v ? 11 : 9), z: b.z - fz * back - fx * side };
      fov = v ? 62 : 42; ease = 3; lookY = 0.3;
    } else if (s.kind === 'orbit') {
      const a = Math.atan2(fx, fz) + (s.dir ?? 1) * (1.2 + this.shotT * 0.55);
      const r = v ? 6.5 : 5.5;
      want = { x: b.x + Math.sin(a) * r, y: b.y + 1.1, z: b.z + Math.cos(a) * r };
      fov = v ? 66 : 44; ease = 8;
    } else {
      // Chase: lower and closer than the ride camera, framed taller in vertical.
      const back = v ? 6.2 : 5.2, up = v ? 2.3 : 1.7;
      want = { x: b.x - fx * back, y: b.y + up, z: b.z - fz * back };
      fov = v ? 72 : 52; ease = 5;
    }
    want.y = Math.max(want.y, ground(want.x, want.z) + 0.35);
    if (this.snap) { this.pos.set(want.x, want.y, want.z); this.look.set(b.x, b.y + lookY, b.z); this.snap = false; } else {
      this.pos.set(damp(this.pos.x, want.x, ease, dt), damp(this.pos.y, want.y, ease, dt), damp(this.pos.z, want.z, ease, dt));
      this.look.set(damp(this.look.x, b.x, 12, dt), damp(this.look.y, b.y + lookY, 12, dt), damp(this.look.z, b.z, 12, dt));
    }
    cam.position.copy(this.pos);
    cam.lookAt(this.look);
    if (Math.abs(cam.fov - fov) > 0.01) { cam.fov = s.kind === 'trackside' ? damp(cam.fov, fov, 4, dt) : fov; cam.updateProjectionMatrix(); }
  }
}

/** Overlays on the 2D composite: letterbox, watermark, title card, slow-motion tag, end card. */
class Compositor {
  readonly canvas = document.createElement('canvas');
  readonly g: CanvasRenderingContext2D;
  private readonly logo = new Image();
  private readonly last = document.createElement('canvas');
  constructor(readonly w: number, readonly h: number, private readonly o: CineOptions) {
    this.canvas.width = w; this.canvas.height = h;
    this.last.width = w; this.last.height = h;
    this.g = this.canvas.getContext('2d')!;
    this.logo.src = LOGO;
  }
  ready(): Promise<void> { return this.logo.decode().catch(() => undefined); }

  private get u(): number { return Math.min(this.w, this.h) / 1080; }
  private get bar(): number { return Math.round(this.w > this.h ? this.h * 0.075 : this.h * 0.045); }

  frame(src: HTMLCanvasElement, t: number, slow: boolean): void {
    const { g, w, h, u } = this;
    g.drawImage(src, 0, 0, w, h);
    const bar = this.bar;
    g.fillStyle = '#000';
    g.fillRect(0, 0, w, bar); g.fillRect(0, h - bar, w, bar);
    this.watermark(1);
    if (t < TITLE_CARD) this.titleCard(Math.min(1, t / 0.4, (TITLE_CARD - t) / 0.4));
    if (slow) {
      g.save();
      g.font = `${Math.round(26 * u)}px 'Russo One', sans-serif`;
      g.fillStyle = 'rgba(242,162,12,.95)';
      g.textAlign = 'left'; g.textBaseline = 'middle';
      g.fillText(`SLOW-MO ${SLOW_SCALE}x`, 40 * u, h - bar - 40 * u);
      g.restore();
    }
    this.last.getContext('2d')!.drawImage(this.canvas, 0, 0);
  }

  private watermark(alpha: number): void {
    const { g, w, u } = this;
    if (!this.logo.naturalWidth) return;
    const lw = (w > this.h ? 250 : 300) * u, lh = lw * (this.logo.naturalHeight / this.logo.naturalWidth);
    g.save(); g.globalAlpha = 0.9 * alpha;
    g.drawImage(this.logo, w - lw - 36 * u, this.bar + 28 * u, lw, lh);
    g.restore();
  }

  private titleCard(a: number): void {
    const { g, h, u } = this;
    const x = 48 * u, y = h - this.bar - (this.w > h ? 70 : 260) * u;
    g.save(); g.globalAlpha = Math.max(0, a);
    g.fillStyle = '#d75706';
    g.beginPath(); g.moveTo(x - 14 * u, y - 70 * u); g.lineTo(x - 2 * u, y - 70 * u); g.lineTo(x - 14 * u, y + 22 * u); g.lineTo(x - 26 * u, y + 22 * u); g.fill();
    g.fillStyle = '#f5f5f5'; g.textBaseline = 'alphabetic'; g.textAlign = 'left';
    g.font = `${Math.round(54 * u)}px 'Russo One', sans-serif`;
    g.shadowColor = 'rgba(0,0,0,.6)'; g.shadowBlur = 12 * u;
    g.fillText(this.o.title.toUpperCase(), x + 10 * u, y - 18 * u);
    if (this.o.time) {
      g.font = `${Math.round(38 * u)}px 'Exo 2', sans-serif`;
      g.fillStyle = '#f2a20c';
      g.fillText(this.o.time, x + 10 * u, y + 24 * u);
    }
    g.restore();
  }

  /** The end card over the last frame: darkened, logo, line, megamotocross.pl. */
  endCard(t: number): void {
    const { g, w, h, u } = this;
    const a = Math.min(1, t / 0.5);
    g.drawImage(this.last, 0, 0);
    g.fillStyle = `rgba(12,10,7,${0.88 * a})`; g.fillRect(0, 0, w, h);
    g.save(); g.globalAlpha = a;
    if (this.logo.naturalWidth) {
      const ar = this.logo.naturalHeight / this.logo.naturalWidth;
      const lw = Math.min(w * 0.6, 560 * u, (h * 0.3) / ar), lh = lw * ar;
      g.drawImage(this.logo, (w - lw) / 2, h * 0.44 - lh, lw, lh);
    }
    g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = '#f5f5f5';
    g.font = `${Math.round(40 * u)}px 'Exo 2', sans-serif`;
    g.fillText([this.o.title, this.o.time].filter(Boolean).join('  ·  '), w / 2, h * 0.5);
    g.font = `${Math.round(36 * u)}px 'Exo 2', sans-serif`; g.fillStyle = '#c9c3b8';
    g.fillText(this.o.endLine, w / 2, h * 0.5 + 62 * u);
    g.font = `${Math.round(64 * u)}px 'Russo One', sans-serif`; g.fillStyle = '#ff7a1a';
    g.fillText('megamotocross.pl', w / 2, h * 0.5 + 160 * u);
    g.restore();
  }
}

/** The recording in progress (main.ts calls tick after every rendered frame). */
let active: { tick(dt: number): void } | null = null;
export function cineTick(dt: number): void { active?.tick(dt); }
export const cineActive = (): boolean => active !== null;

/** The plan the director would make now (for the dialog: is there a ride to film, and how long). */
export function previewPlan(session: GameSession, length: CineLength): CinePlan | null {
  if (session.trace.length < 30) return null;
  return planCinematic(session.trace, levelAnchors(session), length, topHighlight(session.highlights)?.t);
}

/** Whether there is a ride to film: after a finished ride as before; during one, after VIDEO_MIN_RIDE seconds. */
export function canFilm(session: GameSession): boolean {
  if (session.mode === 'ride') return !session.demo && canFilmRide(session.rideSeconds);
  return previewPlan(session, 'short') !== null;
}

export async function recordCinematic(session: GameSession, renderer: THREE.WebGPURenderer, o: CineOptions,
  onProgress: (p: CineProgress) => void, signal: AbortSignal, preview?: (c: HTMLCanvasElement) => void): Promise<CineResult> {
  const plan = previewPlan(session, o.length);
  if (!plan || !session.startReplay()) throw new Error('no ride to film');
  const mime = pickMime((m) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m));
  if (!mime) throw new Error('MediaRecorder unsupported');
  const { w, h } = FORMAT_SIZE[o.format];
  const comp = new Compositor(w, h, o);
  preview?.(comp.canvas);
  await comp.ready();
  onProgress({ phase: 'prepare', f: 0 });
  // Render at the video's size (restored afterwards).
  const view = session.view, pr = renderer.getPixelRatio();
  renderer.setPixelRatio(1);
  const size = (k: number) => { renderer.setSize(Math.round(w * k), Math.round(h * k), false); view.resize(w, h); };
  size(1);
  session.replaySkipTo(plan.start);
  // Warm-up with the replay held: every pipeline the edit uses (depth of field, helmet cam) compiles at this size
  // before the recording starts, and the frame rate picks the render scale (the composite is always full size).
  session.replayScale = 0;
  const warmCam = new CineCamera(session, o.format === 'vertical');
  warmCam.setShot({ kind: 'orbit', from: 0, to: 1 });
  let fps = 60;
  await new Promise<void>((resolve) => {
    let n = 0, t = 0;
    active = {
      tick: (dt) => {
        n++;
        const phase = n < 8 ? 0 : n < 16 ? 1 : 2;
        view.setMenuMode(phase === 0);
        session.replayView = phase === 1 ? 'fpv' : 'chase';
        view.cameraOverride = phase === 1 ? null : (cam, d) => warmCam.apply(cam, d);
        if (n > 24) t += dt;
        if (n >= 44) { fps = 20 / t; resolve(); }
      },
    };
  });
  const k = fps >= 50 ? 1 : fps >= 32 ? 0.8 : 0.66;
  if (k < 1) size(k);
  console.info(`[video] warm-up ${fps.toFixed(0)} fps at full size: rendering at ${Math.round(k * 100)}%`);
  const camera = new CineCamera(session, o.format === 'vertical');
  const total = videoSeconds(plan.start, plan.end, plan.slow) + END_CARD;

  const stream = comp.canvas.captureStream(60);
  const audio = session.audio.tap();
  for (const t of audio?.getAudioTracks() ?? []) stream.addTrack(t);
  const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: o.format === 'vertical' ? 14e6 : 12e6, audioBitsPerSecond: 160e3 });
  const chunks: Blob[] = [];
  rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  const stopped = new Promise<void>((r) => { rec.onstop = () => r(); });

  let videoT = 0, endT = -1, lastKind = '';
  const restore = () => {
    active = null;
    session.view.cameraOverride = null;
    session.view.setMenuMode(false);
    session.replayScale = 1;
    session.replayView = 'chase';
    renderer.setPixelRatio(pr);
    renderer.setSize(innerWidth, innerHeight);
    view.resize(innerWidth, innerHeight);
    session.audio.untap();
    session.stopReplay();
  };
  const done = new Promise<void>((resolve) => {
    const apply = () => {
      const t = session.replayTime;
      const shot = shotAt(plan, t);
      session.replayScale = timeScaleAt(t, plan.slow);
      if (shot.kind !== lastKind) {
        lastKind = shot.kind;
        // Depth of field (the menu pipeline's) on the low and orbit shots; the helmet cam is the view's own.
        session.view.setMenuMode(shot.kind === 'trackside' || shot.kind === 'orbit');
      }
      session.replayView = shot.kind === 'helmet' ? 'fpv' : 'chase';
      session.view.cameraOverride = shot.kind === 'helmet' ? null : (cam, dt) => camera.apply(cam, dt);
      camera.setShot(shot);
    };
    apply();
    active = {
      tick: (dt) => {
        if (signal.aborted) { resolve(); return; }
        if (endT < 0) {
          comp.frame(renderer.domElement, videoT, session.replayScale < 0.8);
          videoT += dt;
          if (session.replayTime >= plan.end || session.replayDone) {
            endT = 0;
            session.replayScale = 0; // hold the last frame under the end card
          } else apply();
        } else {
          endT += dt; videoT += dt;
          comp.endCard(endT);
          if (endT >= END_CARD) resolve();
        }
        onProgress({ phase: 'record', f: Math.min(1, videoT / total) });
      },
    };
  });
  rec.start(500);
  try {
    await done;
  } finally {
    restore();
    rec.stop();
    for (const t of stream.getTracks()) t.stop();
    await stopped;
  }
  if (signal.aborted) throw new DOMException('aborted', 'AbortError');
  onProgress({ phase: 'finish', f: 1 });
  return { blob: new Blob(chunks, { type: mime.split(';')[0] }), mime, seconds: total };
}
